import React, { FC, useMemo, useState } from 'react';
import {
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { Controller, ControllerRenderProps, SubmitHandler, useForm } from 'react-hook-form';
import {
  Device,
  DeviceCreateUpdateBody,
  DeviceHistoryRecordCategory,
  DeviceHistoryRecordType,
  DeviceRmaRecordDto,
} from '@thingslog/repositories';
import { LocalizedDatePicker, useToast } from '@thingslog/ui-components';
import { AxiosError } from 'axios';
import { format, isValid, parse } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { QueryKeys } from '@thingslog/queries/src/enums/QueryKeys';
import { devicesQueryClient } from '../../clients/ReactQueryClients/ReactQueryClients';

const storageDateFormat = 'yyyy-MM-dd';

type View = 'table' | 'form';

interface RmaFormValues {
  date: string | null;
  type: DeviceHistoryRecordType;
  category: DeviceHistoryRecordCategory;
  comments: string | null;
}

interface RmaHistoryModalProps {
  device: Device;
  canAdd: boolean;
}

const parseStorageDate = (value: string | null): Date | null => {
  if (!value) return null;
  const date = parse(value, storageDateFormat, new Date());
  return isValid(date) ? date : null;
};

const buildUpdateBody = (
  device: Device,
  rmaHistory: DeviceRmaRecordDto[]
): DeviceCreateUpdateBody => ({
  number: device.number,
  replacementNumber: device.replacementNumber,
  description: device.description,
  hwVersion: device.hwVersion,
  model: device.model,
  deviceIcon: device.deviceIcon,
  iconId: device.deviceIconDto?.id ?? 0,
  name: device.name,
  swVersion: device.swVersion,
  nomenclature: device.nomenclature,
  ownerCompanyId: device.ownerCompanyId,
  manufacturingDate: device.manufacturingDate,
  warrantyPeriodMonths: device.warrantyPeriodMonths,
  extendedHardwareSupport: device.extendedHardwareSupport,
  language: device.language,
  rmaHistory,
});

const RmaHistoryModal: FC<RmaHistoryModalProps> = ({ device, canAdd }: RmaHistoryModalProps) => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { useUpdateDevice } = useMemo(() => devicesQueryClient, []);

  const [view, setView] = useState<View>('table');
  const [records, setRecords] = useState<DeviceRmaRecordDto[]>(device.rmaHistory ?? []);

  const { control, handleSubmit, reset } = useForm<RmaFormValues>({
    defaultValues: {
      date: format(new Date(), storageDateFormat),
      type: DeviceHistoryRecordType.RMA,
      category: DeviceHistoryRecordCategory.OTHER,
      comments: '',
    },
    mode: 'all',
  });

  const updateDeviceQuery = useUpdateDevice({
    onSuccess: (_data: void, variables: { deviceNumber: string; body: DeviceCreateUpdateBody }) => {
      setRecords(variables.body.rmaHistory ?? []);
      queryClient.invalidateQueries([QueryKeys.UseDeviceData]);
      toast({ type: 'success', message: t('device_inventory_rma_add_success') });
      setView('table');
    },
    onError: (_error: AxiosError) => {
      toast({ type: 'error', message: t('device_inventory_rma_add_error') });
    },
  });

  const onSubmit: SubmitHandler<RmaFormValues> = (data: RmaFormValues) => {
    const newRecord: DeviceRmaRecordDto = {
      id: null,
      date: data.date,
      type: data.type,
      category: data.category,
      comments: data.comments || null,
    };
    const rmaHistory = [...records, newRecord];
    updateDeviceQuery.mutate({
      deviceNumber: device.number,
      body: buildUpdateBody(device, rmaHistory),
    });
  };

  const openForm = (): void => {
    reset({
      date: format(new Date(), storageDateFormat),
      type: DeviceHistoryRecordType.RMA,
      category: DeviceHistoryRecordCategory.OTHER,
      comments: '',
    });
    setView('form');
  };

  if (view === 'form') {
    return (
      <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col mt-2 gap-5">
        <Controller
          name="date"
          control={control}
          rules={{ required: t('rma_record_date_required') as string }}
          render={({
            field,
          }: {
            field: ControllerRenderProps<RmaFormValues, 'date'>;
          }): React.ReactElement => (
            <LocalizedDatePicker
              {...field}
              label={t('rma_record_date')}
              value={parseStorageDate(field.value)}
              onChange={(date: Date | null): void => {
                field.onChange(date ? format(date, storageDateFormat) : null);
              }}
              inputFormat="dd/MM/yyyy"
              renderInputTextField={{ size: 'small', fullWidth: true }}
            />
          )}
        />

        <FormControl fullWidth size="small">
          <InputLabel>{t('rma_record_type')}</InputLabel>
          <Controller
            name="type"
            control={control}
            render={({
              field,
            }: {
              field: ControllerRenderProps<RmaFormValues, 'type'>;
            }): React.ReactElement => (
              <Select {...field} size="small" label={t('rma_record_type')}>
                {Object.values(DeviceHistoryRecordType).map((type: DeviceHistoryRecordType) => (
                  <MenuItem key={type} value={type}>
                    {t(`rma_type_${type}`)}
                  </MenuItem>
                ))}
              </Select>
            )}
          />
        </FormControl>

        <FormControl fullWidth size="small">
          <InputLabel>{t('rma_record_category')}</InputLabel>
          <Controller
            name="category"
            control={control}
            render={({
              field,
            }: {
              field: ControllerRenderProps<RmaFormValues, 'category'>;
            }): React.ReactElement => (
              <Select {...field} size="small" label={t('rma_record_category')}>
                {Object.values(DeviceHistoryRecordCategory).map(
                  (category: DeviceHistoryRecordCategory) => (
                    <MenuItem key={category} value={category}>
                      {t(`rma_category_${category}`)}
                    </MenuItem>
                  )
                )}
              </Select>
            )}
          />
        </FormControl>

        <Controller
          name="comments"
          control={control}
          render={({
            field,
          }: {
            field: ControllerRenderProps<RmaFormValues, 'comments'>;
          }): React.ReactElement => (
            <TextField
              {...field}
              value={field.value ?? ''}
              size="small"
              multiline
              minRows={2}
              label={t('rma_record_comments')}
            />
          )}
        />

        <div className="flex justify-between gap-3">
          <Button
            type="button"
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            disabled={updateDeviceQuery.isLoading}
            onClick={(): void => setView('table')}
          >
            {t('rma_record_back')}
          </Button>
          <Button type="submit" variant="contained" disabled={updateDeviceQuery.isLoading}>
            {t('rma_record_save')}
          </Button>
        </div>
      </form>
    );
  }

  return (
    <div className="flex flex-col mt-2 gap-4">
      {records.length === 0 ? (
        <p className="text-center text-gray-500 my-6">{t('device_inventory_rma_no_records')}</p>
      ) : (
        <TableContainer className="max-h-[55vh] overflow-y-auto">
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>{t('rma_record_date')}</TableCell>
                <TableCell>{t('rma_record_type')}</TableCell>
                <TableCell>{t('rma_record_category')}</TableCell>
                <TableCell>{t('rma_record_comments')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {records.map((record: DeviceRmaRecordDto, index: number) => (
                <TableRow key={record.id ?? `new-${index}`}>
                  <TableCell>
                    {record.date ? format(new Date(record.date), 'dd MMM yyyy') : ''}
                  </TableCell>
                  <TableCell>{t(`rma_type_${record.type}`)}</TableCell>
                  <TableCell>{t(`rma_category_${record.category}`)}</TableCell>
                  <TableCell className="whitespace-pre-wrap break-words">
                    {record.comments}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {canAdd && (
        <Button variant="contained" startIcon={<AddIcon />} onClick={openForm}>
          {t('device_inventory_rma_add_record')}
        </Button>
      )}
    </div>
  );
};

export default RmaHistoryModal;
